import React, { useState, useRef, useEffect } from 'react';
import { Bold, Italic, AlignLeft, AlignCenter, AlignRight, AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd, Minus, Plus, ChevronDown } from 'lucide-react';
import type { Editor } from '@tiptap/react';

interface TextFormatToolbarProps {
  editor: Editor | null;
  verticalAlign?: 'top' | 'middle' | 'bottom';
  onVerticalAlignChange?: (align: 'top' | 'middle' | 'bottom') => void;
}

export const TextFormatToolbar: React.FC<TextFormatToolbarProps> = ({
  editor,
  verticalAlign = 'middle',
  onVerticalAlignChange
}) => {
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowFontDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!editor) return;

    const updateSelection = () => {
      const { from, to } = editor.state.selection;
      const hasContent = from !== to || editor.isFocused;
      setHasSelection(hasContent);
    };

    editor.on('selectionUpdate', updateSelection);
    editor.on('focus', updateSelection);
    editor.on('blur', updateSelection);

    updateSelection();

    return () => {
      editor.off('selectionUpdate', updateSelection);
      editor.off('focus', updateSelection);
      editor.off('blur', updateSelection);
    };
  }, [editor]);

  if (!editor || !hasSelection) return null;

  const fonts = [
    'Test National',
    'Arial',
    'Helvetica Neue',
    'Inter',
    'Georgia',
    'Times New Roman',
    'Courier New',
    'Monaco',
    'Impact',
    'Futura',
    'Garamond',
    'Palatino',
    'Trebuchet MS',
    'Verdana',
    'Comic Sans MS',
  ];

  const increaseFontSize = () => {
    const currentSize = editor.getAttributes('textStyle').fontSize;
    const currentSizeNum = currentSize ? parseInt(currentSize) : 16;
    const newSize = Math.min(currentSizeNum + 4, 200);
    editor.chain().focus().setMark('textStyle', { fontSize: `${newSize}px` }).run();
  };

  const decreaseFontSize = () => {
    const currentSize = editor.getAttributes('textStyle').fontSize;
    const currentSizeNum = currentSize ? parseInt(currentSize) : 16;
    const newSize = Math.max(currentSizeNum - 4, 8);
    editor.chain().focus().setMark('textStyle', { fontSize: `${newSize}px` }).run();
  };

  return (
    <div className="absolute -bottom-20 left-1/2 -translate-x-1/2" style={{ zIndex: 10000 }}>
      <div className="flex items-center gap-1 bg-[#202835] border-2 border-[#DFE4EA] rounded-lg px-3 py-2">
        {/* Bold */}
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            editor.isActive('bold')
              ? 'bg-[rgba(208,226,246,0.3)] text-[#DFE4EA]'
              : 'text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)]'
          }`}
          title="Bold"
        >
          <Bold size={18} />
        </button>

        {/* Italic */}
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            editor.isActive('italic')
              ? 'bg-[rgba(208,226,246,0.3)] text-[#DFE4EA]'
              : 'text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)]'
          }`}
          title="Italic"
        >
          <Italic size={18} />
        </button>

        <div className="w-px h-6 bg-[#DFE4EA] opacity-30 mx-1" />

        {/* Align Left */}
        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            editor.isActive({ textAlign: 'left' })
              ? 'bg-[rgba(208,226,246,0.3)] text-[#DFE4EA]'
              : 'text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)]'
          }`}
          title="Align Left"
        >
          <AlignLeft size={18} />
        </button>

        {/* Align Center */}
        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            editor.isActive({ textAlign: 'center' })
              ? 'bg-[rgba(208,226,246,0.3)] text-[#DFE4EA]'
              : 'text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)]'
          }`}
          title="Align Center"
        >
          <AlignCenter size={18} />
        </button>

        {/* Align Right */}
        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            editor.isActive({ textAlign: 'right' })
              ? 'bg-[rgba(208,226,246,0.3)] text-[#DFE4EA]'
              : 'text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)]'
          }`}
          title="Align Right"
        >
          <AlignRight size={18} />
        </button>

        <div className="w-px h-6 bg-[#DFE4EA] opacity-30 mx-1" />

        {/* Vertical Align Top */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onVerticalAlignChange?.('top');
          }}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            verticalAlign === 'top'
              ? 'bg-[rgba(208,226,246,0.3)] text-[#DFE4EA]'
              : 'text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)]'
          }`}
          title="Align Top"
        >
          <AlignVerticalJustifyStart size={18} />
        </button>

        {/* Vertical Align Middle */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onVerticalAlignChange?.('middle');
          }}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            verticalAlign === 'middle'
              ? 'bg-[rgba(208,226,246,0.3)] text-[#DFE4EA]'
              : 'text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)]'
          }`}
          title="Align Middle"
        >
          <AlignVerticalJustifyCenter size={18} />
        </button>

        {/* Vertical Align Bottom */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onVerticalAlignChange?.('bottom');
          }}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            verticalAlign === 'bottom'
              ? 'bg-[rgba(208,226,246,0.3)] text-[#DFE4EA]'
              : 'text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)]'
          }`}
          title="Align Bottom"
        >
          <AlignVerticalJustifyEnd size={18} />
        </button>

        <div className="w-px h-6 bg-[#DFE4EA] opacity-30 mx-1" />

        {/* Font Family */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowFontDropdown(!showFontDropdown)}
            className="flex items-center gap-1 bg-transparent border border-[#DFE4EA] rounded px-2 py-1 text-[#DFE4EA] text-xs hover:border-white transition-colors min-w-[140px] h-8"
          >
            <span className="flex-1 text-left truncate">
              {editor.getAttributes('textStyle').fontFamily || 'Test National'}
            </span>
            <ChevronDown size={14} />
          </button>
          {showFontDropdown && (
            <div
              className="absolute top-full mt-1 left-0 bg-[#202835] border-2 border-[#DFE4EA] rounded-lg shadow-xl max-h-[300px] overflow-y-auto min-w-[140px] z-[20000]"
              style={{
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale'
              }}
            >
              {fonts.map((font) => (
                <button
                  key={font}
                  onClick={() => {
                    editor.chain().focus().setFontFamily(font).run();
                    setShowFontDropdown(false);
                  }}
                  className="w-full text-left px-3 py-2 text-[#DFE4EA] text-xs hover:bg-[rgba(208,226,246,0.2)] transition-colors"
                  style={{ fontFamily: font }}
                >
                  {font}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-[#DFE4EA] opacity-30 mx-1" />

        {/* Font Size Decrease */}
        <button
          onClick={decreaseFontSize}
          className="w-8 h-8 flex items-center justify-center rounded text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)] transition-colors"
          title="Decrease Font Size"
        >
          <Minus size={18} />
        </button>

        {/* Font Size Increase */}
        <button
          onClick={increaseFontSize}
          className="w-8 h-8 flex items-center justify-center rounded text-[#DFE4EA] hover:bg-[rgba(208,226,246,0.2)] transition-colors"
          title="Increase Font Size"
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
};
